import React, { useEffect, useCallback, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Tooltip from '@material-ui/core/Tooltip'
import IconButton from '@material-ui/core/IconButton'
import Divider from '@material-ui/core/Divider'
import bookActions from 'store/modules/booking'
import bookSelectors from 'store/selectors/booking'
import routerSelectors from 'store/selectors/router'
import routerActions from 'store/modules/router'
import adminActions from 'store/modules/admin'
import bookingActions from 'store/modules/booking'
import adminSelectors from 'store/selectors/admin'
import projectSelectors from 'store/selectors/project'

import Window from 'components/dialog/Window'
import Table from 'components/table/SimpleTable'
import FloatingSidebar from 'components/widgets/FloatingSidebar'
import SlotSummary from 'components/book/SlotSummary'
import EditSlotDialog from 'components/admin/EditSlotDialog'
import EditSlotBlocked from 'components/admin/EditSlotBlocked'
import EditSlotAdminOnly from 'components/admin/EditSlotAdminOnly'

import currencyUtils from 'shared/src/currency'
import Template from 'shared/src/template'
import SlotModel from 'shared/src/slotModel'
import dateUtils from 'shared/src/date'
import icons from 'icons'
import constants from 'shared/src/constants'

const ArrowBackIcon = icons.arrowBack
const EditIcon = icons.edit
const DeleteIcon = icons.delete

const BOOKING_TABLE_SCHEMA = [{
  title: 'Date Booked',
  name: 'date',
  style: {
    fontSize: '0.8em',
  }
}, {
  title: 'Reference',
  name: 'reference',
}, {
  title: 'Status',
  name: 'status',
}, {
  title: 'Paid',
  name: 'paid',
  style: {
    fontSize: '0.8em',
  }
}, {
  title: 'Total',
  name: 'total',
  style: {
    fontSize: '0.8em',
  }
}, {
  title: 'Summary',
  name: 'summary',
  style: {
    fontSize: '0.8em',
  }
}]

const useStyles = makeStyles(theme => ({
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  content: {
    paddingTop: theme.spacing(1),
  },
  sidebarSection: {
    marginBottom: theme.spacing(2),
  },
  button: {
    paddingTop: 0,
    paddingBottom: 0,
    marginBottom: theme.spacing(2),
  },
  buttons: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  backButton: {
    flexGrow: 1,
    textAlign: 'left',
  },
  nextButton: {
    flexGrow: 1,
    textAlign: 'right',
  },
  errors: {
    width: '100%',
    marginTop: '0px',
    marginBottom: theme.spacing(2),
    '& p, li': {
      fontSize: '0.9em',
    },
    '& ul': {
      margin: '0px',
    }
    
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  }
}))

const BookingFormSlot = ({

}) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const [ deletingItem, setDeletingItem ] = useState(null)

  const editingDay = useSelector(bookSelectors.editingDay)
  const editingSlot = useSelector(bookSelectors.editingSlot)
  const slot = useSelector(bookSelectors.slot)
  const formConfig = useSelector(bookSelectors.slotFormConfig)
  const slotOverrideForm = useSelector(bookSelectors.slotOverrideForm)
  const slotOverrideError = useSelector(adminSelectors.updateSlotOverrides.error)
  const editSlotWindowOpen = useSelector(adminSelectors.editSlotWindowOpen)

  const {
    isBlocked,
    canBook,
  } = useMemo(() => {
    const slotModel = SlotModel({
      config: formConfig,
      slot,
    })
    return {
      isBlocked: slotModel.isBlocked(),
      canBook: slotModel.canBook(),
    }
  }, [
    formConfig,
    slot,
  ])

  const {
    id,
    date,
    block_id,
    slot_id,
  } = useSelector(routerSelectors.params)

  const isInPast = useMemo(() => {
    const today = dateUtils.getToday()
    const day = dateUtils.getDate(date)
    return day.getTime() < today.getTime()
  }, [
    date,
  ])

  const addDisabled = isBlocked || !canBook || isInPast

  const project = useSelector(projectSelectors.currentProjectId)

  const onOpenEditSlotWindow = useCallback(() => {
    dispatch(adminActions.setEditSlotWindowOpen(true))
  })

  const onCloseEditSlotWindow = useCallback(() => {
    dispatch(adminActions.setEditSlotWindowOpen(false))
  })

  const getActions = (item) => {
    return (
      <>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={ () => setDeletingItem(item) }
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={ () => {
              dispatch(routerActions.navigateTo('admin.bookingforms.edit.booking', {
                id,
                date,
                block_id,
                slot_id,
                booking_id: item.id,
              }))
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </>
    )
  }

  const onAdd = () => {
    dispatch(routerActions.navigateTo('admin.bookingforms.edit.booking', {
      id,
      date,
      block_id,
      slot_id,
      booking_id: 'new',
    }))
  }

  const onBack = () => {
    dispatch(routerActions.navigateTo('admin.bookingforms.edit.calendar', {
      id,
      date,
    }))
  }

  const onUpdateOverrides = async data => {
    if(editingDay.meta.overridenDay) {
      const newSlots = editingDay.meta.overridenDay.data.slots.map(s => {
        if(s.id != slot_id) return s
        return Object.assign({}, s, {
          data,
        })
      })
      const newData = Object.assign({}, editingDay.meta.overridenDay.data, {
        slots: newSlots,
      })

      const result = await dispatch(adminActions.updateDayOverride({
        project,
        bookingform: id,
        date,
        data: newData,
      }))

      if(result) {
        dispatch(bookingActions.loadCalendar({
          project,
          bookingform: id,
          date,
          days: 1,
          admin: true,
        }))
        dispatch(adminActions.setEditSlotWindowOpen(false))
      }
    }
    else {
      // filter out unchanged values
      const saveData = Object
        .keys(data)
        .filter(name => {
          const newValue = data[name]
          let originalValue = slot[name]
          if(typeof(slot._overriddenValues[name]) !== 'undefined') {
            originalValue = slot._overriddenValues[name]
          }
          return newValue !== originalValue
        })
        .reduce((all, name) => {
          all[name] = data[name]
          return all
        }, {})

      dispatch(adminActions.updateSlotOverrides({
        bookingform: id,
        date,
        block_id: slot.block_id,
        slot_id: slot.id,
        data: saveData,
      }))
    }
  }

  const onResetOverrides = async data => {
    dispatch(adminActions.resetSlotOverrides({
      bookingform: id,
      date,
      block_id: slot.block_id,
      slot_id: slot.id,
    }))
  }

  const onUpdateBlocked = async data => {
    dispatch(adminActions.updateSlotBlocked({
      bookingform: id,
      date,
      block_id: slot.block_id,
      slot_id: slot.id,
      data,
    }))
  }

  const onUpdateAdminOnly = async data => {
    dispatch(adminActions.updateSlotAdminOnly({
      bookingform: id,
      date,
      block_id: slot.block_id,
      slot_id: slot.id,
      data,
    }))
  }

  const onConfirmDelete = async () => {
    const result = await dispatch(adminActions.deleteBooking({
      booking_id: deletingItem.id,
      bookingform: id,
    }))

    if(result) {
      dispatch(bookActions.loadCalendar({
        project,
        bookingform: id,
        date,
        days: 1,
        admin: true,
      }))
      setDeletingItem(null)
    }
  }

  const bookingData = useMemo(() => {
    if(!slot || !slot.bookingRecords) return []
    return slot.bookingRecords.map(booking => {
      const template = Template({
        slot,
        config: formConfig,
        options: booking.data.options,
        details: booking.data.details,
        reference: booking.reference,
        createdAt: booking.created_at,
      })

      const totals = booking.data.totals || {} 
      const admin = booking.data.admin || {}
      const totalCost = totals.total || 0
      const payments = (admin.payments || []).reduce((all, payment) => {
        return all + parseInt(payment.amount)
      }, 0)

      const totalTitle = currencyUtils.title(formConfig.payment.currency, totalCost)
      const paymentsTitle = currencyUtils.title(formConfig.payment.currency, payments)

      const gapMilliseconds = new Date().getTime() - new Date(booking.created_at).getTime()
      const gapMinutes = Math.floor(gapMilliseconds / 1000 / 60)

      return {
        id: booking.id,
        date: new Date(booking.created_at).toLocaleString(),
        status: (
          <div>
            <span>{ booking.status }</span>&nbsp;
            {
              booking.status == constants.BOOKING_STATUS_RESERVED && (
                <span style={{fontSize: '0.8em'}}>
                  ({constants.SLOT_TIMEOUT_MINUTES - gapMinutes} mins left)
                </span>
              )
            }
          </div>
        ),
        reference: booking.reference,
        paid: paymentsTitle,
        total: totalTitle,
        summary: template(formConfig.booking_config.templates.summary, 'booking_config.templates.summary'),
      }
    })
  }, [
    slot,
    formConfig,
  ])

  // convert the URL date into an actual date
  useEffect(() => {
    dispatch(bookActions.loadCalendarConfig({
      project,
      bookingform: id,
    }))
    dispatch(bookActions.loadCalendar({
      project,
      bookingform: id,
      date,
      days: 1,
      admin: true,
    }))
  }, [
    project,
    id,
    date,
  ])

  useEffect(() => {
    if(!editingSlot) return
    dispatch(bookActions.setSlot(editingSlot))
    return () => dispatch(bookActions.setSlot(null))
  }, [
    editingSlot,
  ])

  if(!slot) return null
  if(!formConfig) return null

  return (
    <FloatingSidebar
      sidebarOffset={ 0 }
      sidebar={
        <>
          <div className={ classes.sidebarSection }>
            <div className={ classes.buttons }>
              <div className={ classes.backButton }>
                <Button
                  className={ classes.button }
                  color="primary"
                  onClick={ onBack }
                >
                  <ArrowBackIcon />&nbsp;Calendar
                </Button>
              </div>
            </div>
          </div>
          
          <div className={ classes.sidebarSection }>
            <SlotSummary
              admin
              slot={ slot }
              formConfig={ formConfig }
            />
          </div>

          <div className={ classes.sidebarSection }>
            <EditSlotDialog
              slot={ slot }
              schema={ slotOverrideForm.schema }
              data={ slotOverrideForm.data }
              error={ slotOverrideError }
              open={ editSlotWindowOpen }
              onOpen={ onOpenEditSlotWindow }
              onClose={ onCloseEditSlotWindow }
              onUpdate={ onUpdateOverrides }
              onReset={ onResetOverrides }
            />
          </div>
          <Divider className={ classes.divider } />
          <div className={ classes.sidebarSection }>
            <EditSlotBlocked
              slot={ slot }
              onUpdate={ onUpdateBlocked }
            />
          </div>
          <Divider className={ classes.divider } />
          <div className={ classes.sidebarSection }>
            <EditSlotAdminOnly
              slot={ slot }
              onUpdate={ onUpdateAdminOnly }
            />
          </div>

        </>
      }
    >
      <div className={ classes.content }>
        <Typography gutterBottom variant="h6">
          Bookings
        </Typography>
        <Button
          className={ classes.button }
          variant="contained"
          color="secondary"
          disabled={ addDisabled }
          onClick={ onAdd }
        >
          Add Booking
        </Button>
        <Table
          data={ bookingData }
          fields={ BOOKING_TABLE_SCHEMA }
          getActions={ getActions }
        />
      </div>
      {
        deletingItem && (
          <Window
            open
            withCancel
            title="Confirm delete"
            submitTitle="Delete"
            onCancel={ () => setDeletingItem(null) }
            onSubmit={ onConfirmDelete }
          >
            <Typography>
              Are you absolutely sure you want to delete this booking?
            </Typography>
          </Window>
        )
      }
    </FloatingSidebar>
  )
}

export default BookingFormSlot